<template>
    <div class="product-carousel">
        <!-- Vista desktop: carrusel -->
        <div :id="carouselId" class="carousel slide d-none d-lg-block">
            <div class="carousel-inner">
                <!-- Loading State -->
                <div v-if="isLoading" class="carousel-item active">
                    <div class="row d-flex justify-content-center">
                        <div v-for="n in 4" :key="'skeleton-' + n" class="col-lg-3">
                            <div class="skeleton-loader">
                                <div class="skeleton-image"></div>
                                <div class="skeleton-text w-75 mt-3"></div>
                                <div class="skeleton-text w-50 mt-2"></div>
                                <div class="skeleton-price w-25 mt-3"></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div 
                    v-else
                    v-for="(chunk, index) in productChunks" 
                    :key="index"
                    class="carousel-item"
                    :class="{ active: index === 0 }"
                >
                    <div class="row d-flex justify-content-start">
                        <div 
                            v-for="product in chunk" 
                            :key="product.id"
                            class="col-lg-3 col-6"
                        >
                            <ProductCard 
                                :product="product"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <!-- Carousel indicators -->
            <div class="carousel-indicators" v-if="!isLoading">
                <button
                    v-for="(_, index) in productChunks"
                    :key="index"
                    type="button"
                    :data-bs-target="'#' + carouselId"
                    :data-bs-slide-to="index"
                    :class="{ active: index === 0 }"
                    :aria-current="index === 0"
                    :aria-label="'Slide ' + (index + 1)"
                ></button>
            </div>
        </div>

        <!-- Vista mobile: scroll horizontal -->
        <div class="mobile-scroll d-lg-none">
            <div class="products-row" v-if="!isLoading">
                <div 
                    v-for="product in products" 
                    :key="product.id"
                    class="product-col"
                >
                    <ProductCard :product="product" />
                </div>
            </div>
            <!-- Mobile skeleton loader -->
            <div class="products-row" v-else>
                <div v-for="n in 3" :key="'mobile-skeleton-' + n" class="product-col">
                    <div class="skeleton-loader">
                        <div class="skeleton-image"></div>
                        <div class="skeleton-text w-75 mt-3"></div>
                        <div class="skeleton-text w-50 mt-2"></div>
                        <div class="skeleton-price w-25 mt-3"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ProductCard from '@/components/ProductCard.vue'

export default {
    name: 'ProductCarousel',
    components: {
        ProductCard
    },
    props: {
        products: {
            type: Array,
            required: true,
            default: () => []
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        carouselType: {
            type: String,
            default: 'default'
        }
    },
    data() {
        return {
            uniqueId: Math.random().toString(36).substr(2, 9)
        }
    },
    computed: {
        carouselId() {
            return `productCarousel-${this.carouselType}-${this.uniqueId}`
        },
        productChunks() {
            const chunkSize = 4 // Cambiado a 5 productos por slide
            const chunks = []
            
            for (let i = 0; i < this.products.length; i += chunkSize) {
                chunks.push(this.products.slice(i, i + chunkSize))
            }
            
            return chunks
        }
    }
}
</script>

<style scoped>
.product-carousel {
    width: 100%;
    margin-top: 2rem;
    padding-bottom: 3rem;
}

.carousel-inner {
    padding: 1rem 0;
}

.carousel-indicators {
    position: absolute;
    bottom: -30px;
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
}

.carousel-indicators button {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 1px solid var(--white-color);
    background-color: transparent;
    transition: all 0.3s ease;
}

.carousel-indicators button.active {
    background-color: var(--white-color);
}

.carousel-indicators button:hover {
    background-color: var(--white-color);
}

/* Ensure proper spacing between cards */
.row {
    margin: 0 -15px;
}

.col-lg-3 {
    padding: 0 15px;
}

/* Enhanced Skeleton Loading */
.skeleton-loader {
  background: #1a1a1a;
  padding: 1.5rem;
  border-radius: 0.5rem;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.skeleton-image {
  width: 100%;
  height: 180px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-text {
  height: 16px;
  margin-bottom: 8px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-price {
  height: 24px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-top: auto;
}

@keyframes loading {
  to {
    background-position: -200% 0;
  }
}

.carousel-control-prev,
.carousel-control-next {
    width: 5%;
    opacity: 0.7;
}

.carousel-control-prev:hover,
.carousel-control-next:hover {
    opacity: 1;
}

.carousel-control-prev-icon,
.carousel-control-next-icon {
    background-color: var(--primary-color);
    padding: 1.5rem;
    border-radius: 50%;
}

/* Estilos para vista mobile con scroll horizontal */
.mobile-scroll {
    width: 100%;
    overflow: visible; /* Cambiado de hidden a visible */
    padding: 0.5rem 0;
    position: relative; /* Añadido */
}

.products-row {
    display: flex;
    gap: 1rem;
    padding: 0.5rem;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin; 
    scrollbar-color: var(--primary-color) rgba(255, 255, 255, 0.1);
    margin: 0 -1rem; 
    padding: 0.5rem 1rem; /* Mantener padding interno */
    position: relative; 
    width: calc(100% + 2rem); 
    gap: .2rem; 
}

/* Estilizar scrollbar para webkit (Chrome, Safari, etc) */
.products-row::-webkit-scrollbar {
    display: block;
    height: 8px;
}

.products-row::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
}

.products-row::-webkit-scrollbar-thumb {
    background-color: var(--primary-color);
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: padding-box;
}

.products-row::-webkit-scrollbar-thumb:hover {
    background-color: #15803d; /* Un verde más oscuro al hover */
}

.products-row::-webkit-scrollbar {
    display: none;
}

@media (max-width: 991.98px) {
    .mobile-scroll {
        margin: 0 -1rem; 
        width: calc(100% + 2rem); 
    }

    .product-col:first-child {
        margin-left: 1rem; 
    }

    .product-col:last-child {
        margin-right: 1rem; 
        padding-right: 1rem; 
    }

    .product-col {
        width: 80%; 
    }
}

@media (max-width: 576px) {
    .product-col {
        width: 85%; 
    }
}

.product-col {
    width: 60%; 
    flex: 0 0 auto;
    scroll-snap-align: start;
    padding: 0.5rem;
}
</style>